// src/components/ModalNovaOS.jsx
import React, { useMemo, useState, useCallback } from 'react';
import { FaPlus, FaTrashAlt } from 'react-icons/fa';
import { Search } from 'lucide-react';

export default function ModalNovaOS({
  open,
  onClose,
  onChange,
  onSave,
  form,
  clientes,
  veiculos,
  produtos,
  servicos,
  errors = {},
  editando
}) {
  if (!open) return null;

  const [formErrors, setFormErrors] = useState({});
  const [buscaProduto, setBuscaProduto] = useState('');
  const [buscaServico, setBuscaServico] = useState('');

  const statusOptions = editando
    ? ['Em andamento', 'Finalizada', 'Cancelada']
    : ['Aberta', 'Em andamento'];

  /* ===== Helpers ===== */
  const normalize = useCallback(
    (s) => (s ?? '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
    []
  );
  const norm = (s) => normalize(s);

  const dataAberturaStr = useMemo(() => {
    const d =
      form?.dataAbertura?.toDate?.()
        ? form.dataAbertura.toDate()
        : form?.dataAbertura instanceof Date
          ? form.dataAbertura
          : null;
    if (!d) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, [form?.dataAbertura]);

  /* ===== Buscas (local) ===== */
  const produtosFiltrados = useMemo(() => {
    const q = norm(buscaProduto).trim();
    if (!q) return produtos.slice(0, 50);
    return produtos
      .filter((p) => {
        const nomeIdx = norm(p.nomeSearch || p.nome);
        const sku = norm(p.sku || p.codigo || '');
        const barras = norm(p.codigoBarras || p.codBarras || '');
        return nomeIdx.includes(q) || sku.includes(q) || barras.includes(q);
      })
      .slice(0, 100);
  }, [produtos, buscaProduto, norm]);

  const servicosFiltrados = useMemo(() => {
    const q = norm(buscaServico).trim();
    if (!q) return servicos.slice(0, 50);
    return servicos.filter((s) => norm(s.nome).includes(q)).slice(0, 100);
  }, [servicos, buscaServico, norm]);

  /* ===== Totais ===== */
  const valorTotalProdutos = (form.produtos || []).reduce(
    (acc, p) => acc + (Number(p.precoVenda) || 0) * (Number(p.quantidade) || 0), 0
  );
  const valorTotalServicos = (form.servicos || []).reduce(
    (acc, s) => acc + (Number(s.valor) || 0), 0
  );
  const valorTotalGeral = valorTotalProdutos + valorTotalServicos;

  /* ===== Mutators: Produtos ===== */
  const addProduto = (p) => {
    const atual = [...(form.produtos || [])];
    const idx = atual.findIndex(x => x.produtoId === p.id);
    if (idx >= 0) {
      const clone = [...atual];
      clone[idx] = { ...clone[idx], quantidade: Number(clone[idx].quantidade || 1) + 1 };
      onChange('produtos', clone);
      setFormErrors(e => ({ ...e, [`produto_${idx}`]: undefined }));
      return;
    }
    const novo = {
      produtoId: p.id,
      nome: p.nome,
      sku: p.sku || p.codigo || p.id,
      quantidade: 1,
      precoVenda: Number(p.precoVenda) || Number(p.valor) || 0
    };
    onChange('produtos', [...atual, novo]);
  };

  const updProduto = (idx, field, value) => {
    const arr = [...(form.produtos || [])];
    if (!arr[idx]) return;
    if (field === 'quantidade') {
      arr[idx].quantidade = Math.max(1, Number(value || 1));
    } else if (field === 'precoVenda') {
      arr[idx].precoVenda = Math.max(0, Number(value || 0));
    }
    onChange('produtos', arr);
  };

  const remProduto = (idx) => {
    onChange('produtos', (form.produtos || []).filter((_, i) => i !== idx));
    setFormErrors(e => {
      const c = { ...e };
      delete c[`produto_${idx}`];
      return c;
    });
  };

  /* ===== Mutators: Serviços ===== */
  const addServico = (s) => {
    const atual = [...(form.servicos || [])];
    const novo = {
      servicoId: s.id,
      descricao: s.nome,
      valor: Number(s.valor ?? s.valorSugerido ?? 0)
    };
    onChange('servicos', [...atual, novo]);
  };

  const updServico = (idx, value) => {
    const arr = [...(form.servicos || [])];
    if (!arr[idx]) return;
    arr[idx].valor = Math.max(0, Number(value || 0));
    onChange('servicos', arr);
  };

  const remServico = (idx) => {
    onChange('servicos', (form.servicos || []).filter((_, i) => i !== idx));
    if (formErrors.servicos && (form.servicos || []).length - 1 > 0) {
      setFormErrors(e => ({ ...e, servicos: undefined }));
    }
  };

  /* ===== Validação ===== */
  const handleSubmit = (e) => {
    e.preventDefault();
    const tempErrors = {};

    if (!form.clienteId) tempErrors.clienteId = 'Campo obrigatório';
    if (!form.veiculoId) tempErrors.veiculoId = 'Campo obrigatório';

    if (!form.prazoEstimadoDias || Number(form.prazoEstimadoDias) <= 0) {
      tempErrors.prazoEstimadoDias = 'Informe um prazo válido';
    }
    if (form.kmEntrada !== undefined && form.kmEntrada !== '' && Number(form.kmEntrada) < 0) {
      tempErrors.kmEntrada = 'KM não pode ser negativo';
    }

    // Serviço OPCIONAL: só valida se tiver
    (form.servicos || []).forEach((s) => {
      if (isNaN(Number(s.valor)) || Number(s.valor) < 0) {
        tempErrors.servicos = 'Verifique os valores dos serviços.';
      }
    });

    if (valorTotalGeral <= 0) {
      tempErrors.valorTotal = 'O valor total não pode ser zero.';
    }

    (form.produtos || []).forEach((p, idx) => {
      if (!p.produtoId || !p.sku) tempErrors[`produto_${idx}`] = 'Produto inválido.';
      if (Number(p.quantidade) < 1) tempErrors[`produto_${idx}`] = 'Quantidade mínima é 1.';
    });

    setFormErrors(tempErrors);
    if (Object.keys(tempErrors).length === 0) onSave();
  };

  /* ===== UI helpers ===== */
  const handleClienteChange = (value) => {
    onChange('clienteId', value);
    onChange('veiculoId', '');
    setFormErrors(f => ({ ...f, clienteId: undefined, veiculoId: undefined }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fadein">
      <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-6xl max-h-[94vh] flex flex-col overflow-hidden animate-modalSlideIn">
        {/* Header */}
        <div className="px-8 pt-7 pb-4 border-b border-gray-100 dark:border-gray-800 relative">
          <button
            className="absolute right-8 top-7 text-gray-500 hover:text-[#004072] dark:text-gray-200 dark:hover:text-white text-base font-medium"
            onClick={onClose}
          >
            Fechar
          </button>
          <h2 className="text-2xl font-bold text-blue-600 dark:text-white">
            {editando ? 'Editar OS' : 'Nova OS'}
          </h2>
        </div>

        {/* Body */}
        <form className="flex-1 overflow-y-auto overflow-x-hidden px-8 py-6 space-y-6" onSubmit={handleSubmit}>
          {/* Cliente/Veículo/Status */}
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">
                    Cliente <span className="text-red-600">*</span>
                  </label>
                  <select
                    className={`border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 ${formErrors.clienteId ? 'border-red-500' : ''}`}
                    value={form.clienteId || ''}
                    onChange={e => handleClienteChange(e.target.value)}
                  >
                    <option value="" disabled hidden>Selecione um cliente</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                  {formErrors.clienteId && <span className="text-red-500 text-xs">{formErrors.clienteId}</span>}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">
                    Veículo <span className="text-red-600">*</span>
                  </label>
                  <select
                    className={`border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 ${formErrors.veiculoId ? 'border-red-500' : ''}`}
                    value={form.veiculoId || ''}
                    onChange={e => { setFormErrors(f => ({ ...f, veiculoId: undefined })); onChange('veiculoId', e.target.value); }}
                    disabled={!form.clienteId}
                  >
                    <option value="" disabled hidden>
                      {form.clienteId ? 'Selecione um veículo' : 'Selecione um cliente primeiro'}
                    </option>
                    {veiculos
                      .filter(v => v.clienteId === form.clienteId)
                      .map(v => (
                        <option key={v.id} value={v.id}>
                          {v.placa} - {v.modelo} {v.marca}
                        </option>
                      ))}
                  </select>
                  {formErrors.veiculoId && <span className="text-red-500 text-xs">{formErrors.veiculoId}</span>}
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Prazo estimado (dias) <span className="text-red-600">*</span></label>
                  <input
                    className={`border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 ${formErrors.prazoEstimadoDias ? 'border-red-500' : ''}`}
                    type="number" min={1} value={form.prazoEstimadoDias || ''}
                    onChange={e => { setFormErrors(f => ({ ...f, prazoEstimadoDias: undefined })); onChange('prazoEstimadoDias', e.target.value.replace(/\D/g, '')); }}
                  />
                  {formErrors.prazoEstimadoDias && <span className="text-red-500 text-xs">{formErrors.prazoEstimadoDias}</span>}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">KM na entrada</label>
                  <input
                    className={`border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 ${formErrors.kmEntrada ? 'border-red-500' : ''}`}
                    type="number" min={0} value={form.kmEntrada ?? ''}
                    onChange={e => { onChange('kmEntrada', e.target.value.replace(/\D/g, '')); setFormErrors(f => ({ ...f, kmEntrada: undefined })); }}
                  />
                  {formErrors.kmEntrada && <span className="text-red-500 text-xs">{formErrors.kmEntrada}</span>}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Status</label>
                  <select
                    className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                    value={form.status || 'Aberta'}
                    onChange={e => onChange('status', e.target.value)}
                  >
                    {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {editando && (
                <div className="mt-4">
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Data de abertura (opcional)</label>
                  <input
                    type="datetime-local"
                    className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                    value={dataAberturaStr}
                    onChange={e => onChange('dataAbertura', e.target.value ? new Date(e.target.value) : '')}
                  />
                </div>
              )}
            </div>

            {/* Resumo à direita */}
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 lg:sticky lg:top-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">Resumo da OS</h3>

                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {(form.produtos || []).length === 0 && (form.servicos || []).length === 0 && (
                    <p className="text-gray-500 text-sm">Nenhum item adicionado.</p>
                  )}

                  {/* PRODUTOS */}
                  {(form.produtos || []).map((p, idx) => {
                    const subtotal = (Number(p.quantidade) || 0) * (Number(p.precoVenda) || 0);
                    return (
                      <div key={`p-${idx}`} className="rounded-lg bg-gray-50 dark:bg-gray-700/40 p-2.5">
                        {/* Linha 1: título + SKU + remover */}
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-800 dark:text-gray-200 truncate" title={p.nome}>
                              {p.nome}
                            </p>
                            {!!p.sku && (
                              <span className="inline-flex items-center mt-1 text-[11px] px-1.5 py-0.5 rounded bg-gray-200/80 dark:bg-gray-600/60 text-gray-700 dark:text-gray-200 font-mono">
                                SKU&nbsp;{p.sku}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            className="text-red-600 shrink-0 ml-1 hover:text-red-700"
                            onClick={() => remProduto(idx)}
                            title="Remover"
                          >
                            <FaTrashAlt />
                          </button>
                        </div>

                        {/* Linha 2: Qtd / Preço unit / Subtotal */}
                        <div className="mt-2 grid grid-cols-3 gap-2 items-center">
                          <input
                            type="number"
                            min="1"
                            className="h-9 text-center border rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600"
                            value={p.quantidade}
                            onChange={(e) => updProduto(idx, 'quantidade', e.target.value)}
                            title="Quantidade"
                          />

                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="h-9 text-right border rounded px-2 py-1 font-mono tabular-nums dark:bg-gray-700 dark:border-gray-600"
                            value={p.precoVenda}
                            onChange={(e) => updProduto(idx, 'precoVenda', e.target.value)}
                            title="Preço unitário"
                          />

                          <div className="h-9 flex items-center justify-end font-mono tabular-nums text-gray-700 dark:text-gray-300">
                            R$ {subtotal.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* SERVIÇOS */}
                  {(form.servicos || []).map((s, idx) => (
                    <div key={`s-${idx}`} className="rounded-lg bg-gray-50 dark:bg-gray-700/40 p-2.5">
                      {/* Linha 1: título + tipo + remover */}
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-800 dark:text-gray-200 truncate" title={s.descricao || 'Serviço'}>
                            {s.descricao || 'Serviço'}
                          </p>
                          <span className="inline-flex items-center mt-1 text-[11px] px-1.5 py-0.5 rounded bg-gray-200/80 dark:bg-gray-600/60 text-gray-700 dark:text-gray-200">
                            Serviço
                          </span>
                        </div>
                        <button
                          type="button"
                          className="text-red-600 shrink-0 ml-1 hover:text-red-700"
                          onClick={() => remServico(idx)}
                          title="Remover"
                        >
                          <FaTrashAlt />
                        </button>
                      </div>

                      {/* Linha 2: Valor */}
                      <div className="mt-2 grid grid-cols-3 gap-2 items-center">
                        <div className="col-span-2 text-sm text-gray-500 dark:text-gray-400 pl-1">
                          Valor do serviço
                        </div>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="h-9 text-right border rounded px-2 py-1 font-mono tabular-nums dark:bg-gray-700 dark:border-gray-600"
                          value={s.valor}
                          onChange={(e) => updServico(idx, e.target.value)}
                          title="Valor do serviço"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 border-t pt-3 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                  <div className="flex justify-between"><span>Subtotal Peças:</span><span className="font-medium">R$ {valorTotalProdutos.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>Subtotal Serviços:</span><span className="font-medium">R$ {valorTotalServicos.toFixed(2)}</span></div>
                  <div className="flex justify-between text-lg font-bold mt-2 text-gray-900 dark:text-white">
                    <span>TOTAL:</span><span>R$ {valorTotalGeral.toFixed(2)}</span>
                  </div>
                </div>

                {formErrors.valorTotal && (
                  <div className="text-red-500 text-sm mt-2">{formErrors.valorTotal}</div>
                )}
              </div>
            </div>
          </div>

          {/* Seletor de Itens (estilo Orçamentos) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Produtos */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">Peças / Produtos</h3>
                <span className="text-xs text-gray-500">{produtosFiltrados.length}/{produtos.length}</span>
              </div>

              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full pl-9 pr-8 py-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  placeholder="Pesquisar por nome / nomeSearch / SKU..."
                  value={buscaProduto}
                  onChange={(e) => setBuscaProduto(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && produtosFiltrados.length > 0) {
                      addProduto(produtosFiltrados[0]);
                      setBuscaProduto('');
                    }
                  }}
                />
                {buscaProduto && (
                  <button
                    type="button"
                    onClick={() => setBuscaProduto('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Limpar"
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="space-y-2 max-h-52 overflow-y-auto p-1">
                {produtosFiltrados.length === 0 && (
                  <p className="text-xs text-gray-500 px-1 py-2">Nenhum produto encontrado.</p>
                )}
                {produtosFiltrados.map((p) => {
                  const preco = p.precoVenda ?? p.valor ?? 0;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => addProduto(p)}
                      className="w-full flex items-center gap-3 p-2 text-sm rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                      title="Adicionar produto"
                    >
                      <span className="flex-1 min-w-0 pr-2 truncate">{p.nome}</span>
                      <span className="shrink-0 whitespace-nowrap font-mono tabular-nums text-gray-600 dark:text-gray-400">
                        R$ {(parseFloat(preco) || 0).toFixed(2)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Serviços */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">Mão de Obra / Serviços</h3>
                <span className="text-xs text-gray-500">{servicosFiltrados.length}/{servicos.length}</span>
              </div>

              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full pl-9 pr-8 py-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  placeholder="Pesquisar serviço por nome..."
                  value={buscaServico}
                  onChange={(e) => setBuscaServico(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && servicosFiltrados.length > 0) {
                      addServico(servicosFiltrados[0]);
                      setBuscaServico('');
                    }
                  }}
                />
                {buscaServico && (
                  <button
                    type="button"
                    onClick={() => setBuscaServico('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Limpar"
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="space-y-2 max-h-52 overflow-y-auto p-1">
                {servicosFiltrados.length === 0 && (
                  <p className="text-xs text-gray-500 px-1 py-2">Nenhum serviço encontrado.</p>
                )}
                {servicosFiltrados.map((s) => {
                  const preco = s.valor ?? s.valorSugerido ?? 0;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => addServico(s)}
                      className="w-full flex items-center gap-3 p-2 text-sm rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                      title="Adicionar serviço"
                    >
                      <span className="flex-1 min-w-0 pr-2 truncate">{s.nome}</span>
                      <span className="shrink-0 whitespace-nowrap font-mono tabular-nums text-gray-600 dark:text-gray-400">
                        R$ {(parseFloat(preco) || 0).toFixed(2)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Observações</label>
            <textarea
              className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700"
              placeholder="Observações"
              rows={3}
              value={form.observacoes || ''}
              onChange={e => onChange('observacoes', e.target.value)}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="px-8 pb-6 pt-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
          <button
            type="button"
            onClick={handleSubmit}
            className="w-full py-3 rounded-xl bg-blue-600 text-white text-lg font-semibold flex items-center justify-center gap-2 shadow-lg hover:bg-[#005288] transition"
          >
            <FaPlus /> {editando ? 'Salvar Alterações' : 'Salvar OS'}
          </button>
        </div>
      </div>

      <style>{`
        .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
        @keyframes modalSlideIn {
          0% { opacity:0; transform: translateY(60px) scale(.97);}
          100% { opacity:1; transform: translateY(0) scale(1);}
        }
        .animate-fadein { animation: fadein 0.25s; }
        @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
}
